import type { FunctionComponent, HTMLAttributes } from 'react';
import cx from 'classnames';

import { CardFrontTestIds } from '@trello/test-ids';

import { CARD_ID_ATTRIBUTE } from './CardFront.constants';
import { CardFrontName } from './CardFrontName';
import { useMinimalCardFrontQuery } from './MinimalCardFrontQuery.generated';
import { useCardFrontQueryHookOptions } from './useCardFrontQueryHookOptions';
import { useSetActiveCardOnHoverOrFocus } from './useSetActiveCardOnHoverOrFocus';

import styles from './MinimalCardFront.less';

interface EmbeddedMinimalCardProps extends HTMLAttributes<HTMLDivElement> {
  cardId: string;
  name?: string;
  url?: string;
}

/**
 * Embedded representation of the MinimalCard component. Designed to be used for
 * internal use within the CardFront component, where containing styles and data
 * are already available.
 */
export const EmbeddedMinimalCard: FunctionComponent<
  EmbeddedMinimalCardProps
> = ({ cardId, className, name, url, ...props }) => (
  <div
    className={cx(styles.details, className)}
    data-testid={CardFrontTestIds.MinimalCard}
    {...{ [CARD_ID_ATTRIBUTE]: cardId }}
    {...props}
  >
    <CardFrontName name={name} url={url} />
  </div>
);

interface MinimalCardFrontProps {
  cardId: string;
  skip?: boolean;
}

/**
 * The MinimalCardFront component renders the thinnest possible representation
 * of a card front, with only the card name and required styles.
 */
export const MinimalCardFront: FunctionComponent<MinimalCardFrontProps> = ({
  cardId,
  skip = false,
}) => {
  const { data } = useMinimalCardFrontQuery({
    skip,
    variables: { cardId },
    ...useCardFrontQueryHookOptions(),
  });

  const { onBlur, onFocus, onMouseLeave, onMouseMove } =
    useSetActiveCardOnHoverOrFocus(cardId);

  return (
    <EmbeddedMinimalCard
      cardId={cardId}
      className={styles.cardFront}
      name={data?.card?.name}
      url={data?.card?.url}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    />
  );
};
